import { useState, useRef, useEffect } from "react";
import { cn } from "tailwind-variants";
import { useI18n } from "intl/useI18n";
import { isMediumDeviceAndUp } from "common/utils/browser";
import CommonModal from "common/vue/components/CommonModal.react.vue";
import UseSvg from "common/vue/components/UseSvg.react.vue";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import { useDtfStore } from "../../store";
import Section from "../components/Section.vue";
import RedrawNotes from "../../RedrawNotes.vue";
import RedrawInformation from "../../Modals/RedrawInformation.vue";
import DesignServicesSubscribe from "./DesignServicesSubscribe.vue";
import "./DesignServices.scss";

export default function DesignServices({
  artwork,
  paidExpertEditAddonEnabled,
  index,
  isMultiUpload,
  ...restProps
}) {
  const store = useDtfStore();
  const { n: numberFormatter } = useI18n();

  const [isExpertEditAddonChecked, setIsExpertEditAddonChecked] = useState(
    artwork.hasExpertEditAddon || false
  );
  const [isRedrawModalOpen, setIsRedrawModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(!isMediumDeviceAndUp());
  const redrawModalRef = useRef(null);

  function isStandardDTFProduct() {
    return store.isStandardDTFProduct;
  }

  function showRedrawUI() {
    if (isStandardDTFProduct()) {
      return true;
    }
    return isExpertEditAddonChecked;
  }

  function isLoading() {
    const price = artwork.pricing?.expertEditAddonTierPrice;
    return price === undefined || price === null;
  }

  function isFree() {
    return !isLoading() && artwork.pricing.expertEditAddonTierPrice <= 0;
  }

  function expertEditAddonTierPrice() {
    if (isLoading()) {
      return "";
    }
    if (isFree()) {
      return "FREE";
    }
    return numberFormatter(artwork.pricing.expertEditAddonTierPrice, "currency");
  }

  useEffect(() => {
    setIsExpertEditAddonChecked(artwork.hasExpertEditAddon || false);
  }, [artwork.hasExpertEditAddon]);

  function openRedrawModal() {
    setIsRedrawModalOpen(true);
  }

  function closeRedrawModal() {
    if (redrawModalRef.current) {
      redrawModalRef.current.close();
    }
    setIsRedrawModalOpen(false);
  }

  function onExpertEditAddonToggle(checked) {
    if (artwork.isSimulated) {
      store.setSimulation({
        hasExpertEditAddon: checked
      });
    } else {
      store.updateArtworkValuesById({
        id: artwork.id,
        hasExpertEditAddon: checked,
        enhancementNote: ""
      });
    }
    store.getArtworksPricing();
    if (store.showDesignServicesDeliveryBuffer) {
      if (!checked || (checked && artwork.enhancementNote?.trim())) {
        store.updatePdpDates();
      }
    }
    submitDtfEvent("expert_edit_addon_toggled", {
      artwork_id: artwork.id,
      product_id: store.productId,
      enabled: checked,
      business_line: store.businessLine,
      catalog_number: store.catalogNumber,
      active_config_section: artwork.enhancementTier
    });
  }

  function handleCheckboxChange(checked) {
    onExpertEditAddonToggle(checked);
  }

  return (
    <>
      <Section className="design-services scope-f97dc0b5" data-testid="design-services">
        <div className="design-services__header scope-f97dc0b5">
          {!isStandardDTFProduct() ? (
            <label
              className="design-services__checkbox scope-f97dc0b5"
              id="jiffy-design-services-addon-label"
              data-testid="design-services-checkbox"
            >
              <input
                className="design-services__input scope-f97dc0b5 sr-only"
                checked={isExpertEditAddonChecked}
                onChange={e => {
                  setIsExpertEditAddonChecked(e.target.checked);
                  handleCheckboxChange(e.target.checked);
                }}
                type="checkbox"
                data-testid="print-perfect-design-services-checkbox"
              />

              <div className="design-services__checkbox-box scope-f97dc0b5">
                {isExpertEditAddonChecked ? (
                  <UseSvg
                    className="design-services__checkbox-icon scope-f97dc0b5"
                    icon="check"
                    data-testid="design-services-checkbox-icon"
                  />
                ) : null}
              </div>
              <div className="design-services__label scope-f97dc0b5">
                <div className="design-services__label-text scope-f97dc0b5">
                  {" "}
                  Jiffy Design Services{" "}
                  <span className="design-services__badge scope-f97dc0b5">Add-On</span>
                </div>
                <div className="design-services__help scope-f97dc0b5">
                  {" "}
                  Use our professional designers to edit your image{" "}
                </div>
              </div>
            </label>
          ) : null}
          {!isStandardDTFProduct() ? (
            <div className="design-services__actions scope-f97dc0b5">
              <div
                className={cn("design-services__price scope-f97dc0b5", {
                  "design-services__price--free": isFree()
                })}
              >
                {expertEditAddonTierPrice()}
              </div>
              <button
                className="design-services__learn-more scope-f97dc0b5"
                type="button"
                onClick={openRedrawModal}
                data-testid="design-services-learn-more"
              >
                {" "}
                Learn More{" "}
              </button>
            </div>
          ) : null}
        </div>
        {showRedrawUI() ? (
          <div
            className="design-services__notes js-print-perfect-redraw-notes-addon scope-f97dc0b5"
            data-id={artwork.id}
          >
            <RedrawNotes
              className="scope-f97dc0b5"
              index={index}
              enhancementNote={artwork.enhancementNote}
              {...(isStandardDTFProduct()
                ? {
                    onLearnMore: openRedrawModal
                  }
                : {})}
              data-testid="redraw-notes"
            />
          </div>
        ) : null}
        {!isMultiUpload ? (
          <DesignServicesSubscribe
            className="scope-f97dc0b5"
            data-testid="design-services-subscribe"
          />
        ) : null}
      </Section>
      {}
      {isRedrawModalOpen ? (
        <CommonModal
          className="scope-f97dc0b5"
          ref={redrawModalRef}
          compact={true}
          hideCloseButton={false}
          bottomAligned={isMobile}
          onOnClose={closeRedrawModal}
          data-testid="common-modal"
        >
          <RedrawInformation
            className="scope-f97dc0b5"
            isMobile={isMobile}
            onClose={closeRedrawModal}
            data-testid="redraw-information"
          />
        </CommonModal>
      ) : null}
    </>
  );
}
